import { useRef, useCallback, useEffect, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import {
  Camera,
  PaperclipIcon,
  X,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AgentIcon } from '@/components/common/AgentIcon';
import { CHAT_FONT_FAMILY } from './chat-styles';
import { cn } from '@/lib/utils';
import { VoiceInputButton } from './VoiceInputButton';

export interface ChatInputAttachment {
  documentId: string;
  fileName: string;
  uploading?: boolean;
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (messageOverride?: string) => void;
  isLoading: boolean;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
  size?: 'sm' | 'default';
  /** Auto-focus the input when this becomes true */
  autoFocus?: boolean;
  /** Show a hold-to-speak microphone button that transcribes speech to text */
  enableVoiceInput?: boolean;
  /**
   * Called with the live transcript while recording (partial text), and with
   * `null` when a recording session ends. Use it to surface the in-progress
   * transcription (e.g. mirror it into the input value).
   */
  onVoiceTranscription?: (text: string | null) => void;
  /** Toggle screen sharing mode */
  onShareToggle?: () => void;
  /** Whether screen sharing is currently active */
  isShareActive?: boolean;
  /** Annotation toolbar rendered inside the screenshot info block */
  annotationToolbar?: ReactNode;
  attachments?: ChatInputAttachment[];
  onAttachFile?: () => void;
  onRemoveAttachment?: (documentId: string) => void;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  isLoading,
  onCancel,
  placeholder,
  className,
  textareaClassName,
  size = 'default',
  autoFocus = false,
  enableVoiceInput = false,
  onVoiceTranscription,
  onShareToggle,
  isShareActive = false,
  annotationToolbar,
  attachments,
  onAttachFile,
  onRemoveAttachment,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gt = useGT();
  const resolvedPlaceholder =
    placeholder ??
    gt('Type a message...', { context: 'chat input placeholder' });

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const canSend = isShareActive || !!value.trim();

  const handleSend = useCallback(() => {
    if ((!value.trim() && !isShareActive) || isLoading) return;
    onSend();
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [value, isLoading, isShareActive, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const sizeStyles = {
    sm: {
      textarea: 'min-h-[50px] max-h-[80px] text-sm',
      icon: 20,
    },
    default: {
      textarea: 'min-h-[60px] max-h-[120px]',
      icon: 24,
    },
  };

  const styles = sizeStyles[size];
  const hasAttachments = attachments && attachments.length > 0;

  return (
    <div
      className={cn('flex flex-col', className)}
      style={{ fontFamily: CHAT_FONT_FAMILY }}
    >
      {isShareActive && (
        <div className="rounded-t-md border border-b-0 bg-muted overflow-hidden">
          {annotationToolbar}
          <div className="flex items-center justify-between px-2.5 py-1.5 text-xs text-muted-foreground">
            <span>
              {gt('A screenshot will be included with your message', {
                context:
                  'info text shown when screen capture is active in chat',
              })}
            </span>
            {onShareToggle && (
              <button
                onClick={onShareToggle}
                className="ml-2 rounded p-0.5 hover:bg-accent transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
      <div
        className={cn(
          'rounded-md border bg-background overflow-hidden',
          isShareActive && 'rounded-t-none border-t-0',
        )}
      >
        {hasAttachments && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-2">
            {attachments.map((att) => (
              <div
                key={att.documentId}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-stone-100 border border-stone-200 max-w-[200px]"
              >
                {att.uploading ? (
                  <CircleNotchIcon className="h-3 w-3 animate-spin text-stone-500 shrink-0" />
                ) : (
                  <PaperclipIcon className="h-3 w-3 text-stone-500 shrink-0" />
                )}
                <span className="truncate">{att.fileName}</span>
                {onRemoveAttachment && !att.uploading && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(att.documentId)}
                    className="shrink-0 p-0.5 rounded hover:bg-stone-200"
                  >
                    <X className="h-3 w-3 text-stone-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          className={cn(
            'resize-none border-0 shadow-none focus-visible:ring-0',
            styles.textarea,
            textareaClassName,
          )}
        />
        <div className="flex items-center justify-between px-2 py-1.5 border-t bg-muted/50">
          <div className="flex items-center gap-0.5">
            {enableVoiceInput && (
              <VoiceInputButton
                disabled={isLoading}
                onTranscript={onVoiceTranscription}
                onCommit={onSend}
              />
            )}
            {onAttachFile && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onAttachFile}
                disabled={isLoading}
                className="h-7 w-7 p-0"
                aria-label={gt('Attach file', {
                  context: 'attach file to chat message button',
                })}
              >
                <PaperclipIcon className="h-4 w-4 text-stone-500" />
              </Button>
            )}
            {onShareToggle && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShareToggle}
                className={cn(
                  'h-7 w-7 p-0',
                  isShareActive && 'bg-accent text-accent-foreground',
                )}
                title={
                  isShareActive
                    ? gt('Stop screen capture', {
                        context: 'button tooltip to stop screen capture',
                      })
                    : gt('Capture screen', {
                        context: 'button tooltip to start screen capture',
                      })
                }
              >
                <Camera
                  className="h-4 w-4"
                  weight={isShareActive ? 'fill' : 'regular'}
                />
              </Button>
            )}
          </div>
          {isLoading ? (
            onCancel ? (
              <Button
                onClick={onCancel}
                variant="secondary"
                size="sm"
                className="h-7 px-2"
                aria-label={gt('Cancel', {
                  context: 'cancel AI response button',
                })}
              >
                <AgentIcon state="thinking" size={18} />
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="h-7 px-2"
                disabled
              >
                <AgentIcon state="thinking" size={18} />
              </Button>
            )
          ) : (
            <Button
              onClick={handleSend}
              disabled={!canSend}
              size="sm"
              className="h-7 px-2"
              aria-label={gt('Send message', {
                context: 'send chat message button',
              })}
            >
              <AgentIcon state="send" size={18} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
